import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { Footer } from '@/src/modules/components/Footer';
import { Link } from 'react-router-dom';
import { trackEvent } from '@/src/infrastructure/lib/analytics';
import { BUSINESS_DETAILS } from '@/src/modules/legal/businessDetails';
import { HOST_PRICE_LADDER_INR, PARTNER_PACKS, formatInr } from './pricingCopy';

// Honest interim pricing (Razorpay hotfix, 2026-10-31). Numbers come from
// ./pricingCopy.ts, which cites the #247 pricing source of truth.
// No subscriptions, monthly plans, trials, annual discounts or "unlimited" claims.

type Audience = 'hosts' | 'partners';

interface FaqItem {
  question: string;
  answer: string;
}

const ladderText = HOST_PRICE_LADDER_INR.map(formatInr).join(', ');
const [starter, pro] = PARTNER_PACKS;

export const PRICING_FAQS: FaqItem[] = [
  {
    question: 'Do I need a subscription?',
    answer:
      'No. InviteCloud has no subscriptions or monthly plans. Hosts pay once per invitation. Partners buy prepaid credits when they need them. Nothing renews and nothing is charged automatically.',
  },
  {
    question: 'How much does one invitation cost?',
    answer: `It depends on the design: ${ladderText}. You see the price before you pay, you pay once when you publish, and the price does not change with the number of guests.`,
  },
  {
    question: 'How do prepaid credits for partners work?',
    answer: `You buy a Starter pack (pay ${formatInr(starter.payInr)}, get ${formatInr(starter.creditsInr)} in credits) or a Pro pack (pay ${formatInr(pro.payInr)}, get ${formatInr(pro.creditsInr)} in credits). 1 credit = ₹1, so a ₹1,000 design uses 1,000 credits. Credits never expire, cannot be transferred to anyone else, cannot be withdrawn as cash, and can only be used for invitations on InviteCloud.`,
  },
  {
    question: 'Can I get unused credits back as cash?',
    answer:
      'Unused credits are not paid out as cash. If you bought a pack by mistake, you can ask for a pack refund within the window in our Cancellation & Refund Policy: we refund what you paid minus the credits already used, at ₹1 per credit, to your original payment method.',
  },
  {
    question: 'What if I am charged twice by mistake?',
    answer:
      'If you are a host, the extra payment is refunded to your original payment method (bank account, card or UPI). If you are a partner buying a credit pack, the extra amount is added to your account as prepaid credits (₹1 = 1 credit) instead. Either way, the purchase you meant to make is not affected. Unauthorised transactions are always handled under our Cancellation & Refund Policy and RBI rules.',
  },
  {
    question: 'What happens when my partner credits run out?',
    answer:
      'Invitations you have already published stay live, with your branding and analytics. To publish a new one, buy another pack.',
  },
];

function FaqRow({ item, isOpen, onToggle }: { item: FaqItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border border-outline-variant/30 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full px-6 py-4 flex items-center justify-between bg-surface hover:bg-surface-low transition-colors"
      >
        <h4 className="font-semibold text-on-surface text-left">{item.question}</h4>
        <Icons.ChevronDown
          size={20}
          className={`text-primary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 py-4 bg-surface-lowest text-on-surface-variant text-sm">{item.answer}</div>
      </motion.div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-primary mt-0.5">
        <Icons.Check size={16} />
      </span>
      <span className="text-sm text-on-surface">{children}</span>
    </li>
  );
}

function HostsPanel() {
  return (
    <div className="max-w-3xl mx-auto rounded-3xl p-8 bg-surface border border-outline-variant/30">
      <h2 className="text-2xl font-bold text-on-surface mb-2">
        Pay once per invitation, from {formatInr(HOST_PRICE_LADDER_INR[0])}
      </h2>
      <p className="text-sm text-on-surface-variant mb-6">
        Planning a wedding or a family celebration? Pick a design you love and pay once when you publish it. No
        monthly fees, nothing to cancel later.
      </p>

      <div className="flex flex-wrap gap-3 mb-6" aria-label="Design prices">
        {HOST_PRICE_LADDER_INR.map((price) => (
          <span
            key={price}
            className="px-4 py-2 rounded-full border border-primary/30 bg-primary/5 text-on-surface font-semibold text-sm"
          >
            {formatInr(price)}
          </span>
        ))}
      </div>

      <ul className="space-y-3 mb-8">
        <Bullet>
          <strong>From {formatInr(HOST_PRICE_LADDER_INR[0])} per invitation</strong>, paid once.
        </Bullet>
        <Bullet>
          Designs are priced at {ladderText}, depending on the design. You see the price before you pay.
        </Bullet>
        <Bullet>One price, any number of guests.</Bullet>
        <Bullet>Prices are in Indian Rupees (INR). Payments are processed securely by Razorpay.</Bullet>
      </ul>

      <Link
        to="/designs"
        onClick={() =>
          trackEvent('PUBLIC_CLICK', 'conversion', 'pricing_browse_designs', undefined, {
            element_id: 'pricing_browse_designs',
            section: 'pricing_hosts',
          })
        }
      >
        <Button size="lg" variant="primary">
          Browse designs
        </Button>
      </Link>
    </div>
  );
}

function PartnersPanel() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-on-surface mb-2">Prepaid credits for partners</h2>
        <p className="text-sm text-on-surface-variant max-w-2xl mx-auto">
          Made for wedding planners, photographers, studios and venues who make invitations for many clients. Buy a
          pack of prepaid credits once and use it for every invitation you publish. 1 credit = ₹1, so your maths stays
          simple.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {PARTNER_PACKS.map((pack) => (
          <div
            key={pack.name}
            data-testid={`partner-pack-${pack.name.toLowerCase()}`}
            className="rounded-3xl p-8 bg-surface border border-outline-variant/30"
          >
            <h3 className="text-2xl font-bold text-on-surface mb-4">{pack.name}</h3>
            <p className="text-4xl font-bold text-on-surface">Pay {formatInr(pack.payInr)}</p>
            <p className="text-lg text-primary font-semibold mt-1">Get {formatInr(pack.creditsInr)} in credits</p>
            <p className="text-xs text-on-surface-variant mt-3">One-time payment. Not a subscription.</p>
          </div>
        ))}
      </div>

      <ul className="space-y-3 rounded-3xl p-8 bg-surface border border-outline-variant/30">
        <Bullet>1 credit = ₹1. A ₹1,300 design uses 1,300 credits.</Bullet>
        <Bullet>Credits never expire.</Bullet>
        <Bullet>
          Prepaid credits are non-transferable, cannot be withdrawn as cash, and can be used only for invitations on
          InviteCloud.
        </Bullet>
        <Bullet>
          Every invitation you publish with credits carries your own branding and analytics. Invitations you have
          already published keep them.
        </Bullet>
        <Bullet>No monthly fee, no subscription, nothing charged automatically.</Bullet>
        <Bullet>The extra credits in each pack are your discount; there are no coupons or promotional bonuses.</Bullet>
      </ul>

      <p className="text-sm text-on-surface-variant text-center mt-6">
        Questions about partner packs? Write to us at{' '}
        <a className="text-primary underline" href={`mailto:${BUSINESS_DETAILS.supportEmail}`}>
          {BUSINESS_DETAILS.supportEmail}
        </a>{' '}
        or see our <Link className="text-primary underline" to="/contact-us">Contact Us</Link> page.
      </p>
    </div>
  );
}

export function Pricing() {
  const [audience, setAudience] = useState<Audience>('hosts');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackEvent('PRICING_VIEWED', 'conversion');
  }, []);

  const tabClass = (active: boolean) =>
    `px-6 py-2 rounded-lg font-semibold transition-all ${
      active ? 'bg-primary text-white' : 'text-on-surface-variant hover:text-on-surface'
    }`;

  return (
    <div className="flex flex-col w-full bg-surface-lowest">
      {/* Hero */}
      <section className="relative pt-20 pb-10 lg:pt-28 lg:pb-14 overflow-hidden">
        <div className="absolute top-0 right-0 -mr-[15%] -mt-[10%] w-[50%] h-[70%] rounded-full bg-primary/15 blur-[100px] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-5xl lg:text-6xl font-bold text-on-surface mb-6 leading-tight">
              Honest pricing for <span className="text-primary">every celebration</span>
            </h1>
            <p className="text-lg text-on-surface-variant max-w-2xl mx-auto">
              No monthly plans. No subscriptions. Pay once for the invitation you publish.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Audience tabs */}
      <section className="pb-10">
        <div className="max-w-4xl mx-auto px-6 flex justify-center">
          <div
            role="tablist"
            aria-label="Pricing for"
            className="inline-flex items-center gap-2 bg-surface rounded-xl p-1 border border-outline-variant/30"
          >
            <button
              type="button"
              role="tab"
              aria-selected={audience === 'hosts'}
              onClick={() => setAudience('hosts')}
              className={tabClass(audience === 'hosts')}
            >
              For hosts
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={audience === 'partners'}
              onClick={() => setAudience('partners')}
              className={tabClass(audience === 'partners')}
            >
              For partners
            </button>
          </div>
        </div>
      </section>

      <section className="pb-16 px-6" role="tabpanel">
        {audience === 'hosts' ? <HostsPanel /> : <PartnersPanel />}
        <p className="text-xs text-on-surface-variant text-center mt-8 max-w-2xl mx-auto">
          The price you see is the price you pay. Refunds follow our{' '}
          <Link className="text-primary underline" to="/cancellation-refund-policy">
            Cancellation &amp; Refund Policy
          </Link>
          . Everything we sell is delivered digitally; see our{' '}
          <Link className="text-primary underline" to="/shipping-delivery-policy">
            Shipping &amp; Delivery Policy
          </Link>
          .
        </p>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-outline-variant/20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-on-surface mb-2">Frequently Asked Questions</h2>
            <p className="text-on-surface-variant">Got questions? We've got answers.</p>
          </div>

          <div className="space-y-4">
            {PRICING_FAQS.map((faq, idx) => (
              <FaqRow
                key={faq.question}
                item={faq}
                isOpen={openFaqIndex === idx}
                onToggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
